import { DoseChangeHolds } from "@/features/clinician/DoseChangeHolds";
/**
 * Per-customer detail tabs for the clinical-admin dashboard. Each tab
 * fetches its own endpoint lazily (only when shown) and is cached
 * independently by TanStack Query, so tab switches are instant after the
 * first open.
 */
import { useEffect, useMemo, useState } from 'react';
import { IntakeEvidenceDetails } from '@/features/clinician/IntakeEvidenceDetails';

import { usePharmacyWarnings } from '@/lib/api/adminHooks';
import {
  useAdminUploadDocument,
  useClinicalPatientBilling,
  useClinicalPatientDocuments,
  useClinicalPatientEmr,
  useClinicalPatientIntake,
  useClinicalPatientIntakePreview,
  useClinicalPatientLifecycle,
  useClinicalPatientLabOrders,
  useClinicalPatientOrders,
  useClinicalPatientPrescriptions,
  useClinicalPatientSummary,
  useDeleteClinicalRxTask,
  useRetryOrderRouting,
  useSubscriptionAction,
  useRerunIdCheck,
  useUpdatePatientEmr,
} from '@/lib/api/clinicalAdminHooks';
import type { SubscriptionActionKind } from '@/lib/api/clinicalAdminHooks';
import { reasonCodeLabel, selectableReasonCodes } from '@/lib/orderActionReasons';
import type { OrderActionType } from '@/lib/orderActionReasons';

import { CreateRxTaskModal } from './CreateRxTaskModal';
import type { RxTaskBaseContext } from './CreateRxTaskModal';
import { ErxDetailModal, PdfPreviewModal } from './ErxModals';
import { isReasonValid, ReasonPicker } from './ReasonPicker';
import type { ReasonValue } from './ReasonPicker';
import { SwitchSubscriptionFormatModal } from './SwitchSubscriptionFormatModal';
import { SwitchTreatmentModal } from './SwitchTreatmentModal';
import { PatientTasksPanel } from './PatientTasksPanel';
import { AdminLabRecords } from './AdminLabRecords';
import { IntakeFormAnswers, IntakeOutlierList } from './IntakePreviewPanel';
import { findIntakeOutliers } from '@/features/clinician/intakeOutliers';
import {
  groupOrdersByProduct,
  orderCycleLabels,
  productGroupLabel,
} from './orderGrouping';
import type { OrderGroup } from './orderGrouping';
import {
  isPreviousSubscriptionFormat,
  subscriptionFormatSwitch,
  treatmentFormatLabel,
} from './subscriptionFormatSwitch';
import type { ErxEntry, HealthieTask } from '@/types/clinicalAdmin';
import { chargeBadgePresentation } from './billingPresentation';
import {
  actionLabel,
  fmtAuditTimestamp,
  orderRefDisplay,
  reasonCodeDisplay,
} from './orderActionHistory';

import { PharmacyWarningCard } from '../PharmacyWarningCard';
import type {
  AdminDocumentPurpose,
  EmrUpdateBody,
  OrderLifecycle,
  PatientCard,
  OrderActionLogEntry,
  PatientStorefrontTag,
  PharmacyOrder,
  StripeInvoice,
  StripeSubscription,
} from '@/types/clinicalAdmin';
import { StoreBadge, storefrontOf } from './StoreTags';

import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Td,
  Th,
  Toast,
  fmtDate,
  fmtDateTime,
  fmtMoney,
  onboardingLabel,
} from './ui';

function prettyState(value: string | null | undefined): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ');
}

const PARTNER_LABELS: Record<string, string> = {
  precisionmeds: 'PrecisionMeds',
  the_pharmacy_hub: 'The Pharmacy Hub',
  optiorx: 'OptioRx',
};

function partnerLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return PARTNER_LABELS[value] ?? value;
}

function shortStripeId(value: string | null | undefined): string {
  if (!value) return '—';
  return value.slice(-6).toUpperCase();
}

function normalizedMetadataId(value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  return value.replace(/^superseded:/, '');
}

function subscriptionPlanCycles(subscription: StripeSubscription): number {
  if (subscription.plan_cycles > 0) return subscription.plan_cycles;
  const count = subscription.interval_count || 1;
  if (subscription.interval === 'day') return Math.max(1, Math.round(count / 28));
  if (subscription.interval === 'week') {
    return Math.max(1, Math.round((count * 7) / 28));
  }
  if (subscription.interval === 'month') return Math.max(1, count);
  if (subscription.interval === 'year') return Math.max(1, count * 12);
  return 1;
}

function subscriptionPlanLabel(subscription: StripeSubscription): string {
  if (subscription.plan_label) return subscription.plan_label;
  const cycles = subscriptionPlanCycles(subscription);
  if (cycles === 1) return 'Monthly plan';
  return `${cycles}-month plan`;
}

function planBillingLabel(subscription: StripeSubscription): string {
  const cycles = subscriptionPlanCycles(subscription);
  const cadence = cycles === 1 ? 'every 28 days' : `every ${cycles * 28} days`;
  return `${subscriptionPlanLabel(subscription)} · ${fmtMoney(
    subscription.unit_amount_cents,
    subscription.currency,
  )} ${cadence}`;
}

function pharmacyOrderSummary(orderNumbers: string[]): string {
  if (orderNumbers.length === 0) return 'No linked pharmacy orders';
  const shown = orderNumbers.slice(0, 2).join(', ');
  const remaining = orderNumbers.length - 2;
  return remaining > 0 ? `${shown} +${remaining} more` : shown;
}

// Heuristic carrier detection from a tracking number. Covers UPS + USPS
// (the two carriers our pharmacy partners actually use today); FedEx is
// included as a safety net. Returns null when the format doesn't match
// anything we recognize, so the row falls back to plain text rather than
// linking out to the wrong carrier site.
function getTrackingLink(
  raw: string | null | undefined,
): { carrier: string; url: string } | null {
  if (!raw) return null;
  const n = raw.replace(/\s+/g, '').toUpperCase();
  if (!n) return null;
  // UPS — 1Z + 16 alphanumerics is the canonical format.
  if (/^1Z[A-Z0-9]{16}$/.test(n)) {
    return {
      carrier: 'UPS',
      url: `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(n)}&requester=ST/`,
    };
  }
  // USPS — 20–22 digit numeric is standard tracking; 13-char EXXXXXXXXXUS
  // is Express Mail International.
  if (/^\d{20,22}$/.test(n) || /^9[2345]\d{18,20}$/.test(n) || /^E\w{9}US$/.test(n)) {
    return {
      carrier: 'USPS',
      url: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
    };
  }
  // FedEx — 12 or 15 digit numeric.
  if (/^\d{12}$/.test(n) || /^\d{15}$/.test(n)) {
    return {
      carrier: 'FedEx',
      url: `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
    };
  }
  return null;
}

// --------------------------------------------------------------------------
// Overview tab
// --------------------------------------------------------------------------

export function OverviewTab({
  summary,
  onOpenTicket,
}: {
  summary: PatientCard;
  onOpenTicket?: (ticketId: string) => void;
}) {
  const { data: lifecycle, isLoading: lifecycleLoading } =
    useClinicalPatientLifecycle(summary.id);
  const labs = useClinicalPatientLabOrders(summary.id);
  const labOrders = labs.data?.orders ?? [];
  const latestOrder = [summary.last_order_at, labOrders[0]?.created_at]
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  const rows: { label: string; value: React.ReactNode }[] = [
    {
      label: 'Pharmacy status',
      value: summary.derived_status
        ? prettyState(summary.derived_status)
        : 'No orders',
    },
    { label: labOrders.length ? 'Medication onboarding' : 'Onboarding', value: onboardingLabel(summary.onboarding_state) },
    { label: 'Orders', value: (summary.order_count ?? 0) + labOrders.length },
    {
      label: 'Open warnings',
      value:
        (summary.open_warnings ?? 0) > 0 ? (
          <Badge tone="danger">{summary.open_warnings} open</Badge>
        ) : (
          'None'
        ),
    },
    {
      label: 'Pharmacy partners',
      value:
        summary.partners && summary.partners.length > 0
          ? summary.partners.join(', ')
          : '—',
    },
    { label: 'Last order', value: fmtDate(latestOrder) },
    { label: 'Joined', value: fmtDate(summary.created_at) },
    { label: 'EMR provider', value: summary.emr_provider || '—' },
    { label: 'Healthie ID', value: summary.healthie_id || '—' },
    { label: 'Stripe customer', value: summary.stripe_customer_id || '—' },
    { label: 'Patient ID', value: summary.id },
  ];
  return (
    <div className="flex flex-col gap-16">
      <AdminLabRecords patientId={summary.id} />
      <div className="flex flex-col gap-8">
        <span className="type-label-md font-semibold">
          {labOrders.length ? 'Medication purchase progress' : 'Purchase progress'}
        </span>
        {lifecycleLoading && <LoadingState label="Loading progress…" />}
        {lifecycle && lifecycle.orders.length === 0 && !labs.isLoading && !labs.isError && labs.data && (
          <Card>
            <EmptyState>{labOrders.length ? 'No medication purchases to track yet.' : 'No purchases to track yet.'}</EmptyState>
          </Card>
        )}
        {lifecycle?.orders.map((o) => (
          <OrderLifecycleCard key={o.order_id} order={o} />
        ))}
      </div>

      <PatientTasksPanel patientId={summary.id} onOpenTicket={onOpenTicket} />

      <Card title="Customer summary">
        <table className="w-full type-label-sm">
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.label}
                className="border-t border-line-subtle first:border-0"
              >
                <Td className="text-ink-muted w-[200px]">{r.label}</Td>
                <Td className="text-ink font-mono">{r.value}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function OrderLifecycleCard({ order }: { order: OrderLifecycle }) {
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-8 px-15 py-12 border-b border-line">
        <div className="flex items-center gap-8">
          <span className="type-label-md font-semibold">
            {order.product_label}
          </span>
          {order.order_number && (
            <span className="type-label-sm text-ink-muted">
              {order.order_number}
            </span>
          )}
        </div>
        {order.status && (
          <span className="type-10 uppercase tracking-wide text-ink-muted">
            {prettyState(order.status)}
          </span>
        )}
      </div>
      {order.blocker && (
        <div className="px-15 py-8 bg-danger-wash text-danger type-label-sm border-b border-line">
          {order.blocker}
        </div>
      )}
      <div className="flex flex-wrap gap-8 px-15 py-14">
        {order.steps.map((s) => (
          <div
            key={s.key}
            className={`flex items-center gap-6 type-label-sm px-8 py-4 rounded ${
              s.state === 'current'
                ? 'bg-sunken text-ink font-semibold'
                : s.state === 'done'
                  ? 'text-ink'
                  : 'text-ink-muted'
            }`}
          >
            <span
              className={`inline-flex items-center justify-center w-16 h-16 rounded-full type-10 ${
                s.state === 'done'
                  ? 'bg-ink text-page'
                  : s.state === 'current'
                    ? 'border-2 border-ink'
                    : 'border border-line'
              }`}
            >
              {s.state === 'done' ? '✓' : ''}
            </span>
            {s.label}
          </div>
        ))}
      </div>
    </Card>
  );
}

// --------------------------------------------------------------------------
// EMR tab — editable identity
// --------------------------------------------------------------------------

interface EmrForm {
  first_name: string;
  last_name: string;
  legal_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  sex: 'male' | 'female' | 'other' | 'unknown';
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
}

export function EmrTab({ patientId }: { patientId: string }) {
  const { data, isLoading, isError } = useClinicalPatientEmr(patientId);
  const update = useUpdatePatientEmr(patientId);
  const rerunIdCheck = useRerunIdCheck(patientId);
  const [form, setForm] = useState<EmrForm | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setForm({
        first_name: data.first_name ?? '',
        last_name: data.last_name ?? '',
        legal_name: data.legal_name ?? '',
        email: data.email ?? '',
        phone: data.phone ?? '',
        date_of_birth: data.date_of_birth ?? '',
        sex: (data.sex as EmrForm['sex']) ?? 'unknown',
        line1: data.address?.line1 ?? '',
        line2: data.address?.line2 ?? '',
        city: data.address?.city ?? '',
        state: data.address?.state ?? '',
        postal_code: data.address?.postal_code ?? '',
      });
    }
  }, [data]);

  if (isLoading) return <LoadingState label="Loading EMR record…" />;
  if (isError || !data)
    return (
      <ErrorState>
        Could not load the Healthie record. The patient may have no linked
        EMR account.
      </ErrorState>
    );
  if (!form) return <LoadingState />;

  const set = (key: keyof EmrForm, value: string) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const buildPatch = (): EmrUpdateBody => {
    const patch: EmrUpdateBody = {};
    if (form.first_name !== (data.first_name ?? ''))
      patch.first_name = form.first_name;
    if (form.last_name !== (data.last_name ?? ''))
      patch.last_name = form.last_name;
    if (
      form.legal_name.trim() &&
      form.legal_name.trim() !== (data.legal_name ?? '')
    )
      patch.legal_name = form.legal_name.trim();
    if (form.email !== (data.email ?? '')) patch.email = form.email;
    if (form.phone !== (data.phone ?? '')) patch.phone = form.phone;
    if (form.date_of_birth !== (data.date_of_birth ?? ''))
      patch.date_of_birth = form.date_of_birth;
    if (form.sex !== (data.sex ?? 'unknown')) patch.sex = form.sex;
    const addrChanged =
      form.line1 !== (data.address?.line1 ?? '') ||
      form.line2 !== (data.address?.line2 ?? '') ||
      form.city !== (data.address?.city ?? '') ||
      form.state !== (data.address?.state ?? '') ||
      form.postal_code !== (data.address?.postal_code ?? '');
    if (addrChanged) {
      patch.address = {
        line1: form.line1,
        line2: form.line2 || null,
        city: form.city,
        state: form.state,
        postal_code: form.postal_code,
        country: data.address?.country ?? 'US',
      };
    }
    return patch;
  };

  const onSave = () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      setSavedMsg('No changes to save.');
      return;
    }
    const fields = Object.keys(patch).join(', ');
    if (
      !window.confirm(
        `Apply this correction to the patient's record?\n\nFields: ${fields}\n\n` +
          'EMR fields write to Healthie immediately.',
      )
    )
      return;
    setSavedMsg(null);
    update.mutate(patch, {
      onSuccess: () => setSavedMsg('Saved — record updated.'),
    });
  };

  const idLabels: Record<string, string> = {
    first_name: 'First name',
    middle_name: 'Middle name',
    last_name: 'Last name',
    date_of_birth: 'DOB',
    address_line1: 'Address',
    city: 'City',
    state: 'State',
    postal_code: 'ZIP',
  };

  const rerunButton = (
    <button
      type="button"
      disabled={rerunIdCheck.isPending}
      onClick={() => rerunIdCheck.mutate()}
      className="type-label-sm px-8 py-4 rounded border border-line bg-raised hover:bg-sunken disabled:opacity-40 whitespace-nowrap"
    >
      {rerunIdCheck.isPending
        ? 'Checking…'
        : data.id_info
          ? 'Re-run ID check'
          : 'Run ID check on latest upload'}
    </button>
  );
  const rerunError = rerunIdCheck.isError ? (
    <span className="type-10 text-danger">
      {(rerunIdCheck.error as Error)?.message || 'ID check failed'}
    </span>
  ) : null;

  return (
    <>
    {!data.id_info && (
      <div className="flex items-center gap-8">
        {rerunButton}
        {rerunError}
      </div>
    )}
    {data.id_info && (
      <Card title="ID info (read from uploaded ID)" action={rerunButton}>
        <div className="flex flex-col gap-8">
          {rerunError}
          <div className="flex items-center gap-8">
            <Badge
              tone={
                data.id_info.status === 'verified'
                  ? 'good'
                  : data.id_info.status === 'rejected'
                    ? 'danger'
                    : 'warn'
              }
            >
              {data.id_info.status}
            </Badge>
            <span className="type-10 text-ink-muted">
              Reference only — compare against the EMR fields below; never
              auto-filled (the ID address may differ from shipping).
            </span>
          </div>
          {Object.keys(data.id_info.extracted).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-16 gap-y-6">
              {Object.entries(data.id_info.extracted).map(([k, v]) => (
                <div key={k}>
                  <div className="type-10 text-ink-muted">
                    {idLabels[k] ?? k}
                  </div>
                  <div className="type-label-sm text-ink">{v}</div>
                </div>
              ))}
            </div>
          ) : (
            <span className="type-10 text-ink-muted">
              No fields were readable from the upload.
            </span>
          )}
        </div>
      </Card>
    )}
    <Card
      title="EMR record"
      action={
        <span className="type-label-sm text-ink-muted">
          {data.clinician_name
            ? `Clinician: ${data.clinician_name}`
            : 'No clinician assigned'}
        </span>
      }
    >
      <div className="p-15 grid grid-cols-1 md:grid-cols-2 gap-12">
        <Field label="First name">
          <TextInput
            value={form.first_name}
            onChange={(v) => set('first_name', v)}
          />
        </Field>
        <Field label="Last name">
          <TextInput
            value={form.last_name}
            onChange={(v) => set('last_name', v)}
          />
        </Field>
        <Field label="Legal first name (eRx)">
          <TextInput
            value={form.legal_name}
            onChange={(v) => set('legal_name', v)}
          />
          {form.legal_name.trim().includes(' ') && (
            <p className="mt-1 font-mono uppercase text-12 text-ink-muted">
              First name only. Healthie appends the last name itself, so a
              full name here renders twice (e.g. "Tony Duren Duren").
            </p>
          )}
        </Field>
        <Field label="Date of birth">
          <input
            type="date"
            value={form.date_of_birth}
            onChange={(e) => set('date_of_birth', e.target.value)}
            className="type-label-sm px-10 py-8 rounded border border-line bg-raised w-full"
          />
        </Field>
        <Field label="Email (mirrored to local record)">
          <TextInput value={form.email} onChange={(v) => set('email', v)} />
        </Field>
        <Field label="Phone">
          <TextInput value={form.phone} onChange={(v) => set('phone', v)} />
        </Field>
        <Field label="Sex">
          <select
            value={form.sex}
            onChange={(e) => set('sex', e.target.value)}
            className="type-label-sm px-10 py-8 rounded border border-line bg-raised w-full"
          >
            <option value="unknown">Unknown</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <div className="md:col-span-2 border-t border-line-subtle pt-12 type-label-sm font-semibold text-ink-muted">
          Shipping address
        </div>
        <Field label="Address line 1">
          <TextInput value={form.line1} onChange={(v) => set('line1', v)} />
        </Field>
        <Field label="Address line 2">
          <TextInput value={form.line2} onChange={(v) => set('line2', v)} />
        </Field>
        <Field label="City">
          <TextInput value={form.city} onChange={(v) => set('city', v)} />
        </Field>
        <Field label="State">
          <TextInput value={form.state} onChange={(v) => set('state', v)} />
        </Field>
        <Field label="ZIP">
          <TextInput
            value={form.postal_code}
            onChange={(v) => set('postal_code', v)}
          />
        </Field>
      </div>
      <div className="flex items-center gap-12 px-15 py-12 border-t border-line">
        <button
          type="button"
          onClick={onSave}
          disabled={update.isPending}
          className="type-label-sm px-14 py-8 rounded bg-ink text-page hover:opacity-90 disabled:opacity-50"
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
        {update.isError && (
          <span className="type-label-sm text-danger">
            Save failed — {(update.error as Error)?.message ?? 'unknown error'}
          </span>
        )}
      </div>
    </Card>
    {savedMsg && (
      <Toast message={savedMsg} onDone={() => setSavedMsg(null)} />
    )}
    </>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-4">
      <span className="type-label-sm text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

function TextInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="type-label-sm px-10 py-8 rounded border border-line bg-raised w-full"
    />
  );
}

// --------------------------------------------------------------------------
// Prescriptions tab
// --------------------------------------------------------------------------

export function PrescriptionsTab({ patientId }: { patientId: string }) {
  const { data, isLoading, isError } = useClinicalPatientPrescriptions(patientId);
  // Patient name from the parent-cached summary; state from EMR (loaded
  // by the EMR tab — cached if visited, otherwise fires here so state is
  // ready when the modal opens). Pharmacy + drug context now flows through
  // the modal's Target Medication dropdown (sourced from
  // `orderable_medications` on the prescriptions response), not from a
  // patient-level "primary partner" guess that gets wrong for multi-partner
  // patients (e.g. CA: oral via PPI + injectable via TPH).
  const summaryQ = useClinicalPatientSummary(patientId);
  const emrQ = useClinicalPatientEmr(patientId);
  const deleteTask = useDeleteClinicalRxTask(patientId);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<HealthieTask | null>(null);
  const [detailEntry, setDetailEntry] = useState<ErxEntry | null>(null);
  const [pdfEntry, setPdfEntry] = useState<ErxEntry | null>(null);

  const taskBaseContext: RxTaskBaseContext = useMemo(() => {
    const displayName = summaryQ.data?.display_name ?? '';
    const firstName = displayName.split(/\s+/)[0] || null;
    return { firstName, state: emrQ.data?.address?.state ?? null };
  }, [summaryQ.data, emrQ.data]);

  const onDeleteTask = async (t: HealthieTask) => {
    const ok = window.confirm(
      `Delete this Write-Rx task from the prescriber's queue?\n\n"${(t.content ?? '').slice(0, 200)}"\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    try {
      await deleteTask.mutateAsync(t.id);
    } catch (e) {
      window.alert(
        e instanceof Error ? e.message : 'Could not delete task.',
      );
    }
  };

  if (isLoading) return <LoadingState label="Loading prescriptions…" />;
  if (isError || !data)
    return <ErrorState>Could not load prescriptions.</ErrorState>;

  return (
    <div className="flex flex-col gap-16">
      <DoseChangeHolds patientId={patientId} readOnly />
      <Card title={`eRx on file (${data.erx.length})`}>
        {data.erx.length === 0 ? (
          <EmptyState>No DoseSpot eRx or PDF script on file.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Type</Th>
                  <Th>Name</Th>
                  <Th>Directions</Th>
                  <Th>Status</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {data.erx.map((rx, i) => {
                  const isDose = rx.type === 'dosespot';
                  const clickable = isDose || rx.pdf_available;
                  const onRowClick = () => {
                    if (isDose) setDetailEntry(rx);
                    else if (rx.pdf_available) setPdfEntry(rx);
                  };
                  return (
                    <tr
                      key={i}
                      className={`border-t border-line-subtle ${
                        clickable ? 'cursor-pointer hover:bg-sunken/60' : ''
                      }`}
                      onClick={clickable ? onRowClick : undefined}
                    >
                      <Td>
                        {isDose ? (
                          <Badge tone="accent">DoseSpot</Badge>
                        ) : rx.pdf_available ? (
                          <Badge tone="neutral">PDF</Badge>
                        ) : (
                          <Badge tone="warn">PDF — pending</Badge>
                        )}
                      </Td>
                      <Td>{String(rx.name ?? '—')}</Td>
                      <Td>{String(rx.directions ?? '—')}</Td>
                      <Td>{String(rx.status ?? '—')}</Td>
                      <Td>
                        <span className="text-ink-muted">
                          {isDose
                            ? 'Details ›'
                            : rx.pdf_available
                              ? 'Preview ›'
                              : '—'}
                        </span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title={`Write-Rx tasks (${data.tasks.length})`}
        action={
          <button
            type="button"
            onClick={() => {
              setEditingTask(null);
              setTaskModalOpen(true);
            }}
            className="h-30 px-12 bg-ink text-page type-label-sm rounded hover:opacity-90"
          >
            + Clinician request
          </button>
        }
      >
        <p className="px-15 py-8 type-body-xs text-ink-muted border-b border-line-subtle">
          Healthie prescription tasks appear in the clinician worklist. Completing a task does not resolve a dose-change hold.
        </p>
        {data.tasks.length === 0 ? (
          <EmptyState>No clinician tasks.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Created</Th>
                  <Th>Task</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {data.tasks.map((t) => (
                  <tr key={t.id} className="border-t border-line-subtle">
                    <Td>{fmtDate(t.created_at)}</Td>
                    <Td>{t.content ?? '—'}</Td>
                    <Td>
                      <Badge tone={t.complete ? 'good' : 'warn'}>
                        {t.complete ? 'Done' : 'Open'}
                      </Badge>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-6">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingTask(t);
                            setTaskModalOpen(true);
                          }}
                          disabled={t.complete}
                          title={
                            t.complete
                              ? 'Completed tasks cannot be edited'
                              : 'Edit task'
                          }
                          className="h-26 px-8 bg-sunken text-ink type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onDeleteTask(t)}
                          disabled={deleteTask.isPending}
                          title="Delete task"
                          className="h-26 px-8 bg-danger-wash text-danger type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Delete
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {taskModalOpen && (
        <CreateRxTaskModal
          patientId={patientId}
          baseContext={taskBaseContext}
          orderableMedications={data.orderable_medications ?? []}
          existingTask={
            editingTask
              ? { id: editingTask.id, content: editingTask.content ?? '' }
              : null
          }
          onClose={() => {
            setTaskModalOpen(false);
            setEditingTask(null);
          }}
        />
      )}

      {detailEntry && (
        <ErxDetailModal
          entry={detailEntry}
          onClose={() => setDetailEntry(null)}
        />
      )}
      {pdfEntry && (
        <PdfPreviewModal
          patientId={patientId}
          entry={pdfEntry}
          onClose={() => setPdfEntry(null)}
        />
      )}

      <Card title={`Medications (${data.medications.length})`}>
        {data.medications.length === 0 ? (
          <EmptyState>No medications on the Healthie chart.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Name</Th>
                  <Th>Dose</Th>
                  <Th>Frequency</Th>
                </tr>
              </thead>
              <tbody>
                {data.medications.map((m, i) => (
                  <tr key={i} className="border-t border-line-subtle">
                    <Td>{String(m.name ?? '—')}</Td>
                    <Td>{String(m.dose ?? '—')}</Td>
                    <Td>{String(m.frequency ?? '—')}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------------
// Orders & Pharmacy tab
// --------------------------------------------------------------------------

export function OrdersTab({ patientId }: { patientId: string }) {
  return <div className="flex flex-col gap-16"><AdminLabRecords patientId={patientId} /><PharmacyOrdersTab patientId={patientId} /></div>;
}

function PharmacyOrdersTab({ patientId }: { patientId: string }) {
  const { data, isLoading, isError } = useClinicalPatientOrders(patientId);
  const billing = useClinicalPatientBilling(patientId);
  const warnings = usePharmacyWarnings('all', patientId);
  // The patient's storefronts (the header's summary, cached): an order that
  // names only its storefront id still shows the store's name.
  const knownStores =
    useClinicalPatientSummary(patientId).data?.storefronts ?? [];
  const [expanded, setExpanded] = useState<string | null>(null);

  if (isLoading) return <LoadingState label="Loading orders…" />;
  if (isError || !data) return <ErrorState>Could not load orders.</ErrorState>;

  const warningRows = warnings.data?.warnings ?? [];
  const subscriptionsById = new Map(
    (billing.data?.subscriptions ?? []).map((subscription) => [
      subscription.id,
      subscription,
    ]),
  );
  const invoicesById = new Map(
    (billing.data?.invoices ?? []).map((invoice) => [invoice.id, invoice]),
  );

  return (
    <div className="flex flex-col gap-16">
      {warningRows.length > 0 && (
        <div className="flex flex-col gap-12">
          <span className="type-label-md font-semibold">
            Pharmacy warnings · {warnings.data?.unresolved_count ?? 0} open
          </span>
          {warningRows.map((w) => (
            <PharmacyWarningCard
              key={`${w.order_id}-${w.reason_code}-${w.received_at}`}
              w={w}
            />
          ))}
        </div>
      )}
      {billing.isError && (
        <ErrorState>
          Pharmacy orders loaded, but Stripe billing links could not be loaded.
          Order fulfillment is unaffected.
        </ErrorState>
      )}
      {data.orders.length === 0 ? (
        <Card>
          <EmptyState>No pharmacy orders for this customer.</EmptyState>
        </Card>
      ) : (
        <Card title={`Pharmacy orders (${data.orders.length})`}>
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Order</Th>
                  <Th>Partner</Th>
                  <Th>Status</Th>
                  <Th>Billing link</Th>
                  <Th>Created</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {groupOrdersByProduct(data.orders).map((group) => (
                  <ProductOrderGroup
                    key={group.key}
                    group={group}
                    patientId={patientId}
                    subscription={
                      group.purchaseId
                        ? subscriptionsById.get(group.purchaseId) ?? null
                        : null
                    }
                    invoicesById={invoicesById}
                    knownStores={knownStores}
                    expanded={expanded}
                    onToggle={(id) =>
                      setExpanded(expanded === id ? null : id)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function ProductOrderGroup({
  group,
  patientId,
  subscription,
  invoicesById,
  knownStores,
  expanded,
  onToggle,
}: {
  group: OrderGroup;
  patientId: string;
  subscription: StripeSubscription | null;
  invoicesById: Map<string, StripeInvoice>;
  knownStores: PatientStorefrontTag[];
  expanded: string | null;
  onToggle: (id: string) => void;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const hasIssue = group.current.some(
    (o) => o.status === 'failed' && o.partner_key === 'unrouted',
  );
  const liveStatuses = Array.from(
    new Set(group.current.map((o) => o.status || '—')),
  );
  const cycleLabels = orderCycleLabels(group);
  const previousFormat = subscription
    ? isPreviousSubscriptionFormat(group.productId, subscription)
    : false;
  const formatSwitch = subscription
    ? subscriptionFormatSwitch(subscription)
    : null;
  return (
    <>
      <tr className="border-t border-line bg-sunken/60">
        <td colSpan={6} className="px-12 py-8">
          <div className="flex items-center justify-between gap-12 flex-wrap">
            <div className="flex items-center gap-8 flex-wrap">
              <span className="type-label-sm font-semibold text-ink">
                {productGroupLabel(group.productId)}
              </span>
              {subscription && (
                <Badge tone="neutral">
                  {subscriptionPlanLabel(subscription)}
                </Badge>
              )}
              {previousFormat && <Badge tone="warn">Previous format</Badge>}
              {group.purchaseIdShort && (
                <span
                  className="type-10 font-mono text-ink-muted bg-raised border border-line rounded px-6 py-1"
                  title={
                    group.purchaseKind === 'subscription'
                      ? 'Stripe subscription id (last 6)'
                      : group.purchaseKind === 'one-time'
                        ? 'Stripe session id (last 6)'
                        : 'order id (first 6)'
                  }
                >
                  {group.purchaseKind === 'subscription'
                    ? 'SUB'
                    : group.purchaseKind === 'one-time'
                      ? 'PI'
                      : 'ORDER'}
                  ·{group.purchaseIdShort}
                </span>
              )}
              {subscription && !previousFormat && (
                <span className="type-10 text-ink-muted">
                  {planBillingLabel(subscription)}
                </span>
              )}
              {previousFormat && formatSwitch && (
                <span className="type-10 text-ink-muted">
                  Subscription switched to{' '}
                  {treatmentFormatLabel(formatSwitch.toProductId)}
                  {formatSwitch.switchedAt
                    ? ` on ${fmtDate(formatSwitch.switchedAt)}`
                    : ''}
                </span>
              )}
            </div>
            <div className="flex items-center gap-8 type-label-sm text-ink-muted">
              {hasIssue && <Badge tone="danger">needs review</Badge>}
              <span>
                {group.current.length} orders
                {group.current.length > 0 && liveStatuses.length > 0
                  ? ` · ${liveStatuses.join(', ')}`
                  : ''}
                {group.history.length > 0
                  ? ` · ${group.history.length} historical`
                  : ''}
              </span>
            </div>
          </div>
        </td>
      </tr>
      {group.current.map((o) => (
        <OrderRows
          key={o.id}
          order={o}
          patientId={patientId}
          open={expanded === o.id}
          onToggle={() => onToggle(o.id)}
          cycleLabel={cycleLabels.get(o.id) ?? null}
          groupProductId={group.productId}
          subscription={subscription}
          knownStores={knownStores}
          invoice={
            invoicesById.get(
              normalizedMetadataId(o.metadata?.stripe_invoice_id) ?? '',
            ) ?? null
          }
        />
      ))}
      {group.history.length > 0 && (
        <tr>
          <td colSpan={6} className="px-12 py-6">
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="type-label-sm text-ink-muted hover:text-ink"
            >
              {showHistory
                ? `Hide ${group.history.length} superseded`
                : `Show ${group.history.length} superseded`}
            </button>
          </td>
        </tr>
      )}
      {showHistory &&
        group.history.map((o) => (
          <OrderRows
            key={o.id}
            order={o}
            patientId={patientId}
            open={expanded === o.id}
            onToggle={() => onToggle(o.id)}
            cycleLabel={cycleLabels.get(o.id) ?? null}
            groupProductId={group.productId}
            subscription={subscription}
            knownStores={knownStores}
            invoice={
              invoicesById.get(
                normalizedMetadataId(o.metadata?.stripe_invoice_id) ?? '',
              ) ?? null
            }
          />
        ))}
    </>
  );
}

function OrderRows({
  order,
  patientId,
  open,
  onToggle,
  cycleLabel,
  groupProductId,
  subscription,
  knownStores,
  invoice,
}: {
  order: PharmacyOrder;
  patientId: string;
  open: boolean;
  onToggle: () => void;
  cycleLabel: string | null;
  groupProductId: string;
  subscription: StripeSubscription | null;
  knownStores: PatientStorefrontTag[];
  invoice: StripeInvoice | null;
}) {
  const store = storefrontOf(order, knownStores);
  const warnings =
    ((order.metadata?.warnings as unknown[] | undefined) ?? []).length;
  const actionLog = order.action_log ?? [];
  // Routing failed pre-flight (partner rejected creation, e.g. missing DOB).
  // These rows can be retried after the upstream data gap is fixed.
  const routingFailed =
    order.status === 'failed' && order.partner_key === 'unrouted';
  const routingError =
    typeof order.metadata?.routing_error === 'string'
      ? (order.metadata.routing_error as string)
      : null;
  const sessionId =
    typeof order.metadata?.stripe_session_id === 'string'
      ? (order.metadata.stripe_session_id as string)
      : '';
  const alreadyRetried = sessionId.startsWith('superseded:');
  // Parked pre-transmission — the only state where the treatment can still
  // be switched (patient changed their mind on dosage form/med).
  const switchable = order.status === 'awaiting_prescription';
  const [switching, setSwitching] = useState(false);
  const metaMedicationName =
    typeof order.metadata?.medication_name === 'string'
      ? order.metadata.medication_name
      : null;
  const selectedTierLabel =
    order.product_id && order.product_id !== groupProductId
      ? metaMedicationName || order.product_id
      : null;
  const schedule = order.shipment_schedule;
  const prepaidLaterFill = !!schedule && schedule.shipment_index > 1;
  const invoiceDisplay =
    invoice?.number || (invoice?.id ? `…${shortStripeId(invoice.id)}` : null);

  // Hook lives at the row level so the inline button (in the actions cell)
  // and the routing-error info panel (in the expanded section) share state.
  const retry = useRetryOrderRouting(patientId);

  const onRetry = () => {
    const label = order.order_number || order.id.slice(0, 8);
    const ok = window.confirm(
      `Retry routing on ${label}?\n\n` +
        (routingError ? `Original error: ${routingError}\n\n` : '') +
        `Fix the upstream data (DOB, address, etc.) before retrying — otherwise it'll just fail again and create another failed row.`,
    );
    if (!ok) return;
    retry.mutate(order.id);
  };

  return (
    <>
      <tr className="border-t border-line-subtle">
        <Td>
          <div>{order.order_number || order.id.slice(0, 8)}</div>
          {(cycleLabel || selectedTierLabel) && (
            <div className="type-10 text-ink-muted mt-2">
              {[cycleLabel, selectedTierLabel].filter(Boolean).join(' · ')}
            </div>
          )}
          {store && (
            <div className="mt-4">
              <StoreBadge store={store} />
            </div>
          )}
        </Td>
        <Td>{partnerLabel(order.partner_key)}</Td>
        <Td>
          <div className="flex items-center gap-6 flex-wrap">
            <span>{order.status || '—'}</span>
            {typeof order.pharmacy_stage === 'string' &&
              order.pharmacy_stage && (
                <span
                  title="In-pharmacy step (Optio poll / PPI webhook) — informational only"
                  className="type-10 font-mono uppercase tracking-wide px-6 py-1 rounded border border-line text-ink-muted"
                >
                  {order.pharmacy_stage}
                </span>
              )}
            {(order.status === 'shipped' || order.status === 'delivered') &&
              (() => {
                const link = getTrackingLink(order.tracking_number);
                if (!link) return null;
                return (
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Tracking ${order.tracking_number} (${link.carrier})`}
                    className="type-10 font-mono uppercase tracking-wide px-6 py-1 rounded border border-line text-ink-muted hover:text-ink hover:border-ink"
                  >
                    Track ↗ {link.carrier}
                  </a>
                );
              })()}
            {routingFailed && <Badge tone="danger">routing failed</Badge>}
            {warnings > 0 && <Badge tone="danger">{warnings} warn</Badge>}
            {actionLog.length > 0 && (
              <Badge tone="warn">
                {actionLabel(actionLog[0].action)}
                {actionLog[0].replacement?.id === order.id
                  ? ' · replacement'
                  : ''}
              </Badge>
            )}
          </div>
        </Td>
        <Td>
          <div className="flex flex-col gap-2 max-w-[260px]">
            {prepaidLaterFill ? (
              <>
                <span className="font-semibold text-ink">
                  Included in{' '}
                  {subscription
                    ? subscriptionPlanLabel(subscription)
                    : 'prepaid plan'}
                </span>
                <span className="type-10 text-ink-muted">
                  Fill {schedule.shipment_index} of {schedule.total_shipments}
                  {' · '}no new charge
                  {invoiceDisplay ? ` · invoice ${invoiceDisplay}` : ''}
                </span>
              </>
            ) : invoice ? (
              <>
                <span className="font-semibold text-ink">
                  Part of invoice {invoiceDisplay}
                </span>
                <span className="type-10 text-ink-muted">
                  {fmtMoney(invoice.amount_paid, invoice.currency)} total paid on
                  invoice
                  {subscription
                    ? ` · ${subscriptionPlanLabel(subscription)}`
                    : ''}
                </span>
              </>
            ) : subscription ? (
              <>
                <span className="font-semibold text-ink">
                  {subscriptionPlanLabel(subscription)}
                </span>
                <span className="type-10 text-ink-muted">
                  SUB · {shortStripeId(subscription.id)}
                </span>
              </>
            ) : (
              <span className="text-ink-muted">No linked invoice</span>
            )}
          </div>
        </Td>
        <Td>{fmtDate(order.created_at)}</Td>
        <Td>
          <div className="flex items-center gap-10 whitespace-nowrap">
            {routingFailed && (
              <button
                type="button"
                disabled={retry.isPending || alreadyRetried}
                onClick={onRetry}
                title={routingError || undefined}
                className="type-label-sm px-10 py-4 rounded border border-danger text-danger bg-danger-wash hover:opacity-90 disabled:opacity-40"
              >
                {retry.isPending
                  ? 'Retrying…'
                  : alreadyRetried
                    ? 'Already retried'
                    : 'Retry routing'}
              </button>
            )}
            {switchable && (
              <button
                type="button"
                onClick={() => setSwitching(true)}
                className="type-label-sm px-10 py-4 rounded border border-line text-ink-muted hover:text-ink hover:border-ink"
              >
                Switch treatment
              </button>
            )}
            <button
              type="button"
              onClick={onToggle}
              className="type-label-sm text-accent hover:underline"
            >
              {open ? 'Hide' : 'Logs'}
            </button>
          </div>
        </Td>
      </tr>
      {switching && (
        <SwitchTreatmentModal
          patientId={patientId}
          order={order}
          onClose={() => setSwitching(false)}
        />
      )}
      {open && (
        <tr className="bg-sunken">
          <td colSpan={6} className="px-12 py-10">
            <div className="flex flex-col gap-8">
              {routingFailed && (
                <RoutingErrorInfo
                  routingError={routingError}
                  alreadyRetried={alreadyRetried}
                  retry={retry}
                />
              )}
              <OrderActionHistory entries={actionLog} orderId={order.id} />
              <DetailLine label="Partner order ID" value={order.partner_order_id} />
              <DetailLine
                label="Stripe subscription"
                value={
                  normalizedMetadataId(order.metadata?.stripe_subscription_id) ||
                  '—'
                }
              />
              <DetailLine
                label="Stripe invoice"
                value={
                  invoice?.number ||
                  normalizedMetadataId(order.metadata?.stripe_invoice_id) ||
                  '—'
                }
              />
              <DetailLine
                label="Plan"
                value={subscription ? planBillingLabel(subscription) : '—'}
              />
              <DetailLine
                label="Shipment"
                value={
                  schedule
                    ? `Fill ${schedule.shipment_index} of ${schedule.total_shipments} · cycle ${schedule.cycle_index} · ${schedule.status}`
                    : 'Not a scheduled prepaid fill'
                }
              />
              <DetailLine
                label="Catalog snapshot"
                value={`${fmtMoney(order.total_cents)} · not necessarily a separate charge`}
              />
              <DetailLine label="Tracking" value={order.tracking_number} />
              <DetailLine label="Shipped" value={fmtDateTime(order.shipped_at)} />
              <DetailLine
                label="Delivered"
                value={fmtDateTime(order.delivered_at)}
              />
              <JsonBlock label="Metadata" value={order.metadata} />
              <JsonBlock
                label="Last partner payload"
                value={order.raw_partner_payload}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RoutingErrorInfo({
  routingError,
  alreadyRetried,
  retry,
}: {
  routingError: string | null;
  alreadyRetried: boolean;
  retry: ReturnType<typeof useRetryOrderRouting>;
}) {
  const result = retry.data;
  return (
    <div className="border border-danger bg-danger-wash rounded p-12 flex flex-col gap-6">
      <span className="type-label-sm font-semibold text-danger">
        Routing failed — order never reached the pharmacy
      </span>
      <span className="type-label-sm text-ink">
        {routingError || 'No routing_error recorded.'}
      </span>
      <span className="type-10 text-ink-muted">
        Fix the upstream data (DOB, address, etc.) in Healthie / Stripe,
        then click <strong>Retry routing</strong> in the row to replay the
        original Stripe event through the pipeline.
      </span>
      {alreadyRetried && !result && (
        <span className="type-10 text-ink-muted italic">
          This row has already been retried — see the newer row above.
        </span>
      )}
      {retry.isError && (
        <span className="type-label-sm text-danger">
          Retry failed: {(retry.error as Error)?.message || String(retry.error)}
        </span>
      )}
      {result && !retry.isError && (
        <span className="type-label-sm text-ink">
          {result.already_processed
            ? `Already processed — order ${result.pharmacy_order_id ?? ''}.`
            : `Replay succeeded → ${result.partner_key ?? 'partner'} · ${result.status ?? 'pending'} (order ${result.pharmacy_order_id ?? ''}).`}
        </span>
      )}
    </div>
  );
}

function DetailLine({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex gap-8 type-label-sm">
      <span className="text-ink-muted w-[160px] shrink-0">{label}</span>
      <span className="text-ink font-mono break-all">{value || '—'}</span>
    </div>
  );
}

function OrderActionHistory({
  entries,
  orderId,
}: {
  entries: OrderActionLogEntry[];
  orderId: string;
}) {
  return (
    <div className="flex flex-col gap-8">
      <span className="type-label-sm font-semibold text-ink">
        Workflow history
      </span>
      {entries.length === 0 && (
        <span className="type-label-sm text-ink-muted">
          No reship, refire, rescript or cancel actions recorded for this
          order.
        </span>
      )}
      {entries.map((entry) => (
        <div
          key={entry.id}
          className="border border-line bg-raised rounded p-10 flex flex-col gap-6"
        >
          <span className="type-label-sm font-semibold text-ink">
            {actionLabel(entry.action)}
            {entry.replacement?.id === orderId
              ? ' · this order is the replacement'
              : entry.replacement
                ? ' · this order is the original'
                : ''}
          </span>
          <DetailLine label="Recorded actor" value={entry.actor} />
          <DetailLine
            label="Selected reason code"
            value={reasonCodeDisplay(entry.reason_code)}
          />
          <DetailLine
            label="Written reason"
            value={entry.reason || 'Not provided'}
          />
          {entry.scope && <DetailLine label="Scope" value={entry.scope} />}
          <DetailLine label="Original" value={orderRefDisplay(entry.original)} />
          {entry.replacement && (
            <DetailLine
              label="Replacement"
              value={orderRefDisplay(entry.replacement)}
            />
          )}
          <DetailLine label="Logged" value={fmtAuditTimestamp(entry.logged_at)} />
          <DetailLine
            label="Pharmacy note"
            value={
              entry.pharmacy_note ? (
                <span className="font-sans whitespace-pre-wrap break-words">
                  {entry.pharmacy_note}
                </span>
              ) : (
                'None recorded'
              )
            }
          />
        </div>
      ))}
    </div>
  );
}

function JsonBlock({
  label,
  value,
}: {
  label: string;
  value: unknown;
}) {
  if (value == null) return null;
  return (
    <div className="flex flex-col gap-4">
      <span className="type-label-sm text-ink-muted">{label}</span>
      <pre className="type-10 bg-raised border border-line rounded p-8 overflow-x-auto max-h-[260px]">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

// --------------------------------------------------------------------------
// Billing tab
// --------------------------------------------------------------------------

export function BillingTab({ patientId }: { patientId: string }) {
  return <div className="flex flex-col gap-16"><AdminLabRecords patientId={patientId} tab="billing" /><SubscriptionBillingTab patientId={patientId} /></div>;
}

function SubscriptionBillingTab({ patientId }: { patientId: string }) {
  const { data, isLoading, isError } = useClinicalPatientBilling(patientId);
  const knownStores =
    useClinicalPatientSummary(patientId).data?.storefronts ?? [];
  if (isLoading) return <LoadingState label="Loading billing…" />;
  if (isError || !data) return <ErrorState>Could not load billing.</ErrorState>;
  if (!data.stripe_customer_id)
    return (
      <Card>
        <EmptyState>No Stripe customer linked to this patient.</EmptyState>
      </Card>
    );

  const subscriptionsById = new Map(
    data.subscriptions.map((subscription) => [
      subscription.id,
      subscription,
    ]),
  );

  return (
    <div className="flex flex-col gap-16">
      {data.errors.length > 0 && (
        <ErrorState>
          Some Stripe data could not be loaded: {data.errors.join('; ')}
        </ErrorState>
      )}

      <Card title={`Subscriptions (${data.subscriptions.length})`}>
        {data.subscriptions.length === 0 ? (
          <EmptyState>No subscriptions.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Plan / subscription</Th>
                  <Th>Product</Th>
                  <Th>Status</Th>
                  <Th>Billing</Th>
                  <Th>Pharmacy fulfillment</Th>
                  <Th>Renews</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {data.subscriptions.map((s) => {
                  const canceled =
                    s.status === 'canceled' || s.status === 'cancelled';
                  const formatSwitch = subscriptionFormatSwitch(s);
                  const store = storefrontOf(s, knownStores);
                  return (
                    <tr key={s.id} className="border-t border-line-subtle">
                      <Td>
                        <div className="flex flex-col gap-2 min-w-[130px]">
                          <span className="font-semibold">
                            {subscriptionPlanLabel(s)}
                          </span>
                          <span
                            className="type-10 font-mono text-ink-muted"
                            title={s.id}
                          >
                            SUB · {shortStripeId(s.id)}
                          </span>
                          <span className="type-10 text-ink-muted">
                            Started {fmtDate(s.created)}
                          </span>
                          {store && (
                            <span className="mt-2">
                              <StoreBadge store={store} />
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div className="flex flex-col gap-2">
                          <span>{s.product_name || s.id}</span>
                          {formatSwitch && (
                            <div className="flex flex-col items-start gap-2">
                              <Badge tone="accent">Format switched</Badge>
                              <span className="type-10 text-ink-muted">
                                From{' '}
                                {treatmentFormatLabel(
                                  formatSwitch.fromProductId,
                                )}
                                {formatSwitch.switchedAt
                                  ? ` on ${fmtDate(formatSwitch.switchedAt)}`
                                  : ''}
                                . Future renewals use{' '}
                                {treatmentFormatLabel(formatSwitch.toProductId)}.
                              </span>
                            </div>
                          )}
                          {s.bundle_items && s.bundle_items.length > 0 && (
                            <ul className="type-10 text-ink-muted list-disc list-inside">
                              {s.bundle_items.map((p) => (
                                <li key={p}>{p}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div className="flex flex-col gap-2">
                          <Badge
                            tone={
                              canceled
                                ? 'danger'
                                : s.paused
                                  ? 'warn'
                                  : s.status === 'active' ||
                                      s.status === 'trialing'
                                    ? 'good'
                                    : 'warn'
                            }
                          >
                            {/* The backend now normalizes status to our business
                                states (paused / active / canceled / past_due) —
                                Stripe's raw 'trialing' (a future-dated renewal via
                                trial_end, never a real free trial) is mapped to
                                'active' there. This trialing→active fallback is
                                belt-and-suspenders in case a raw status slips
                                through; the real next-bill date is in Renews. */}
                            {canceled
                              ? 'canceled'
                              : s.paused
                                ? 'paused'
                                : s.status === 'trialing'
                                  ? 'active'
                                  : s.status || '—'}
                          </Badge>
                          {s.cancel_at_period_end && !canceled && (
                            <Badge tone="warn">cancels at period end</Badge>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div className="flex flex-col gap-2 min-w-[120px]">
                          <span>{fmtMoney(s.unit_amount_cents, s.currency)}</span>
                          <span className="type-10 text-ink-muted">
                            Billed every {subscriptionPlanCycles(s) * 28} days
                          </span>
                        </div>
                      </Td>
                      <Td>
                        <div className="flex flex-col gap-2 max-w-[220px]">
                          <span>
                            {pharmacyOrderSummary(
                              s.fulfillment?.pharmacy_order_numbers ?? [],
                            )}
                          </span>
                          {s.fulfillment?.latest_order_status && (
                            <span className="type-10 text-ink-muted">
                              Latest:{' '}
                              {prettyState(s.fulfillment.latest_order_status)}
                              {(s.fulfillment.active_order_count ?? 0) > 0
                                ? ` · ${s.fulfillment.active_order_count} active`
                                : ''}
                            </span>
                          )}
                          {s.routing?.status === 'will_reroute' && (
                            <div className="flex flex-col items-start gap-2">
                              <Badge tone="warn">Reroutes next fill</Badge>
                              <span className="type-10 text-ink-muted">
                                {PARTNER_LABELS[s.routing.current_partner ?? ''] ??
                                  s.routing.current_partner}{' '}
                                →{' '}
                                {PARTNER_LABELS[s.routing.next_partner ?? ''] ??
                                  s.routing.next_partner}{' '}
                                for {s.routing.patient_state}
                              </span>
                            </div>
                          )}
                          {s.routing?.status === 'blocked' && (
                            <div className="flex flex-col items-start gap-2">
                              <Badge tone="danger">Next fill blocked</Badge>
                              <span className="type-10 text-danger">
                                No compatible pharmacy for{' '}
                                {s.routing.patient_state || 'the current address'}.
                              </span>
                            </div>
                          )}
                          {(s.routing?.status === 'unchanged' ||
                            s.routing?.status === 'new_route') && (
                            <span className="type-10 text-ink-muted">
                              Next fill:{' '}
                              {PARTNER_LABELS[s.routing.next_partner ?? ''] ??
                                s.routing.next_partner}{' '}
                              · {s.routing.patient_state}
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td>
                        {canceled
                          ? `Canceled ${fmtDate(s.canceled_at)}`
                          : s.cancel_at_period_end
                            ? `Ends ${fmtDate(s.current_period_end)}`
                            : s.paused
                              ? `Paused · resumes ${fmtDate(s.next_renewal_at ?? s.current_period_end)}`
                              : `Renews ${fmtDate(s.next_renewal_at ?? s.current_period_end)}`}
                      </Td>
                      <Td>
                        <SubscriptionActions
                          sub={s}
                          canceled={canceled}
                          patientId={patientId}
                        />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title={`Invoices (${data.invoices.length})`}>
        {data.invoices.length === 0 ? (
          <EmptyState>No invoices.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Invoice</Th>
                  <Th>Status</Th>
                  <Th>Subscription / plan</Th>
                  <Th>Reason</Th>
                  <Th>Paid</Th>
                  <Th>Pharmacy fulfillment</Th>
                  <Th>Created</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => {
                  const subscription = inv.subscription
                    ? subscriptionsById.get(inv.subscription) ?? null
                    : null;
                  return (
                    <tr key={inv.id} className="border-t border-line-subtle">
                      <Td>
                        <div className="flex flex-col gap-2">
                          <span>{inv.number || inv.id}</span>
                          <span
                            className="type-10 font-mono text-ink-muted"
                            title={inv.id}
                          >
                            INV · {shortStripeId(inv.id)}
                          </span>
                        </div>
                      </Td>
                      <Td>{inv.status || '—'}</Td>
                      <Td>
                        {subscription ? (
                          <div className="flex flex-col gap-2 min-w-[130px]">
                            <span>
                              {subscription.product_name || 'Subscription'}
                            </span>
                            <span className="type-10 text-ink-muted">
                              {subscriptionPlanLabel(subscription)} · SUB{' '}
                              {shortStripeId(subscription.id)}
                            </span>
                          </div>
                        ) : inv.subscription ? (
                          <span
                            className="font-mono type-10"
                            title={inv.subscription}
                          >
                            SUB · {shortStripeId(inv.subscription)}
                          </span>
                        ) : (
                          <span className="text-ink-muted">
                            Not a subscription
                          </span>
                        )}
                      </Td>
                      <Td>{inv.billing_reason || '—'}</Td>
                      <Td>{fmtMoney(inv.amount_paid, inv.currency)}</Td>
                      <Td>
                        <div className="flex flex-col gap-2 max-w-[220px]">
                          <span>
                            {pharmacyOrderSummary(
                              inv.fulfillment?.pharmacy_order_numbers ?? [],
                            )}
                          </span>
                          {(inv.fulfillment?.pharmacy_order_numbers.length ??
                            0) > 0 && (
                            <span className="type-10 text-ink-muted">
                              This invoice funded these fills
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td>{fmtDate(inv.created)}</Td>
                      <Td>
                        {inv.hosted_invoice_url && (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-accent hover:underline"
                          >
                            Open
                          </a>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title={`Charges (${data.charges.length})`}>
        {data.charges.length === 0 ? (
          <EmptyState>No charges.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Refunded</Th>
                  <Th>Failure</Th>
                  <Th>Created</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {data.charges.map((c) => (
                  <tr key={c.id} className="border-t border-line-subtle">
                    <Td>{fmtMoney(c.amount, c.currency)}</Td>
                    <Td>
                      <Badge tone={chargeBadgePresentation(c).tone}>
                        {chargeBadgePresentation(c).label}
                      </Badge>
                    </Td>
                    <Td>
                      {c.amount_refunded
                        ? fmtMoney(c.amount_refunded, c.currency)
                        : '—'}
                    </Td>
                    <Td>{c.failure_message || '—'}</Td>
                    <Td>{fmtDate(c.created)}</Td>
                    <Td>
                      {c.receipt_url && (
                        <a
                          href={c.receipt_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-accent hover:underline"
                        >
                          Receipt
                        </a>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function SubscriptionActions({
  sub,
  canceled,
  patientId,
}: {
  sub: StripeSubscription;
  canceled: boolean;
  patientId: string;
}) {
  const action = useSubscriptionAction(patientId);
  const label = sub.product_name || sub.id;
  // Explicit backend flag (NOT status==='trialing', which is also true for a
  // sub in its initial trial or one resumed back to a future renewal).
  const paused = sub.paused === true;
  // Default-checked: a Cancel & refund also recalls the current cycle's pending
  // pharmacy order (the backend skips it automatically if it already shipped).
  const [cancelPharmacyOrder, setCancelPharmacyOrder] = useState(true);
  const [showFormatSwitch, setShowFormatSwitch] = useState(false);

  // 'cancel', 'refund', and 'cancel-and-refund' are all logged to the order
  // action audit trail — the backend requires a reason_code for each. A
  // window.prompt can't render a dropdown, so these three go through a
  // small modal (reasonModal state below) instead of firing immediately.
  const REASON_MODAL_KINDS: SubscriptionActionKind[] = [
    'cancel',
    'refund',
    'cancel-and-refund',
  ];
  // 'cancel' here is the plain subscription cancel (Stripe only, no
  // refund) — maps to the order_action_log action 'cancel_subscription',
  // NOT 'cancel' (which is the pharmacy-order-cancel taxonomy).
  const REASON_MODAL_ACTION_TYPE: Record<string, OrderActionType> = {
    cancel: 'cancel_subscription',
    refund: 'refund',
    'cancel-and-refund': 'cancel_and_refund',
  };
  const REASON_MODAL_TITLES: Record<string, string> = {
    cancel: 'Cancel subscription',
    refund: 'Refund',
    'cancel-and-refund': 'Cancel & refund',
  };
  const [reasonModal, setReasonModal] = useState<{
    kind: 'cancel' | 'refund' | 'cancel-and-refund';
    confirmMsg: string;
  } | null>(null);
  const [reasonValue, setReasonValue] = useState<ReasonValue>({
    reasonCode: '',
    reason: '',
  });

  const fire = (kind: SubscriptionActionKind, confirmMsg: string) => {
    if (REASON_MODAL_KINDS.includes(kind)) {
      setReasonValue({ reasonCode: '', reason: '' });
      setReasonModal({
        kind: kind as 'cancel' | 'refund' | 'cancel-and-refund',
        confirmMsg,
      });
      return;
    }
    if (!window.confirm(confirmMsg)) return;
    action.mutate({ action: kind, subId: sub.id });
  };

  const confirmReasonModal = () => {
    if (!reasonModal || !isReasonValid(reasonValue)) return;
    action.mutate({
      action: reasonModal.kind,
      subId: sub.id,
      ...(reasonModal.kind === 'cancel-and-refund' ? { cancelPharmacyOrder } : {}),
      reasonCode: reasonValue.reasonCode,
      reason: reasonValue.reason,
    });
    setReasonModal(null);
  };

  // One control for both directions: a FUTURE date delays the next bill +
  // shipment (effectively a pause); TODAY bills + ships the next refill right
  // now (e.g. a patient travelling who needs it early). The backend confirms
  // the charge cleared before transmitting — a declined card ships nothing.
  const fireChangeRenewalDate = () => {
    const today = new Date().toISOString().slice(0, 10);
    const newDate = window.prompt(
      `Change the next renewal date for "${label}".\n\n` +
        `• A FUTURE date delays the next bill + shipment (a pause).\n` +
        `• TODAY (${today}) bills now and ships the next refill immediately.\n\n` +
        `Enter the new renewal date (YYYY-MM-DD, today … 180 days out):`,
      today,
    );
    if (!newDate) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate.trim())) {
      window.alert('Enter the date as YYYY-MM-DD.');
      return;
    }
    const pullingForward = newDate.trim() <= today;
    if (
      pullingForward &&
      !window.confirm(
        `This will BILL "${label}" now and transmit the next refill to the ` +
          `pharmacy. Continue?`,
      )
    ) {
      return;
    }
    const reason =
      window.prompt('Reason (for the audit trail):') ?? '';
    action.mutate({
      action: 'change-renewal-date',
      subId: sub.id,
      newRenewalDate: newDate.trim(),
      reason,
    });
  };

  const pending = action.isPending;
  const currentKind =
    pending && action.variables?.subId === sub.id
      ? action.variables.action
      : null;
  const showResult =
    action.variables?.subId === sub.id &&
    (action.data || action.isError) &&
    !pending;

  const btn =
    'type-label-sm px-8 py-4 rounded border border-line bg-raised hover:bg-sunken disabled:opacity-40 whitespace-nowrap';
  const btnDanger =
    'type-label-sm px-8 py-4 rounded border border-danger text-danger bg-danger-wash hover:opacity-90 disabled:opacity-40 whitespace-nowrap';

  return (
    <div className="flex flex-col gap-4 min-w-[200px]">
      <div className="flex items-center gap-6 flex-wrap">
        {!canceled && (
          <button
            type="button"
            disabled={pending}
            onClick={() =>
              fire(
                'cancel',
                `Cancel "${label}" immediately?\n\nThis stops future billing on this subscription. Past charges are NOT refunded.`,
              )
            }
            className={btn}
          >
            {currentKind === 'cancel' ? 'Cancelling…' : 'Cancel'}
          </button>
        )}
        {!canceled && paused && (
          <button
            type="button"
            disabled={pending}
            onClick={() =>
              fire(
                'resume',
                `Resume "${label}" now?\n\nBilling and shipments pick back up — no re-bill if the originally-paid period hasn't ended yet.`,
              )
            }
            className={btn}
          >
            {currentKind === 'resume' ? 'Resuming…' : 'Resume'}
          </button>
        )}
        {!canceled && (
          <button
            type="button"
            disabled={pending || paused || Boolean(sub.bundle_items?.length)}
            onClick={() => setShowFormatSwitch(true)}
            className={btn}
            title={
              paused
                ? 'Resume the subscription before changing its format.'
                : sub.bundle_items?.length
                  ? 'Bundle formats cannot be changed independently.'
                  : 'Change dosage form beginning with the next renewal.'
            }
          >
            Switch next renewal…
          </button>
        )}
        {!canceled && (
          <button
            type="button"
            disabled={pending}
            onClick={fireChangeRenewalDate}
            className={btn}
          >
            {currentKind === 'change-renewal-date'
              ? 'Updating…'
              : 'Change Renewal Date…'}
          </button>
        )}
        <button
          type="button"
          disabled={pending}
          onClick={() =>
            fire(
              'refund',
              `Refund the latest charge on "${label}"?\n\nThis issues a full refund of the most recent invoice. The subscription stays active.`,
            )
          }
          className={btn}
        >
          {currentKind === 'refund' ? 'Refunding…' : 'Refund latest'}
        </button>
        {!canceled && (
          <button
            type="button"
            disabled={pending}
            onClick={() =>
              fire(
                'cancel-and-refund',
                `Cancel "${label}" AND refund the latest charge?\n\nFull refund of the most recent invoice + subscription cancelled immediately.${
                  cancelPharmacyOrder
                    ? '\n\nThe current pending pharmacy order will also be recalled if it has not yet shipped.'
                    : ''
                }`,
              )
            }
            className={btnDanger}
          >
            {currentKind === 'cancel-and-refund'
              ? 'Cancelling + refunding…'
              : 'Cancel & refund'}
          </button>
        )}
      </div>
      {!canceled && (
        <label className="flex items-start gap-4 type-10 text-ink-muted select-none">
          <input
            type="checkbox"
            checked={cancelPharmacyOrder}
            onChange={(e) => setCancelPharmacyOrder(e.target.checked)}
            disabled={pending}
            className="mt-2"
          />
          <span>
            On <b>Cancel &amp; refund</b>, also recall the pending pharmacy order
            (skipped automatically if it already shipped).
          </span>
        </label>
      )}
      {showResult && action.isError && (
        <span className="type-10 text-danger">
          Failed: {(action.error as Error)?.message || String(action.error)}
        </span>
      )}
      {showResult && action.data && !action.isError && (
        <span className="type-10 text-ink-muted">
          {action.data.refund_amount_cents != null
            ? `refunded $${(action.data.refund_amount_cents / 100).toFixed(2)} `
            : ''}
          {action.data.subscription_status
            ? `· sub ${action.data.subscription_status} `
            : ''}
          {action.data.detail ? `· ${action.data.detail}` : ''}
          {action.data.pharmacy_recall &&
          !action.data.pharmacy_recall.error &&
          ((action.data.pharmacy_recall.recalled?.length ?? 0) > 0 ||
            (action.data.pharmacy_recall.could_not_cancel?.length ?? 0) > 0)
            ? ` · recalled ${
                action.data.pharmacy_recall.recalled?.length ?? 0
              } pharmacy order(s)${
                action.data.pharmacy_recall.could_not_cancel?.length
                  ? `, ${action.data.pharmacy_recall.could_not_cancel.length} could not (already shipped)`
                  : ''
              }`
            : ''}
          {action.data.pharmacy_recall?.error
            ? ' · pharmacy recall failed'
            : ''}
        </span>
      )}
      {reasonModal && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center p-24 bg-ink/60 backdrop-blur-sm overflow-y-auto"
          onClick={() => {
            if (!pending) setReasonModal(null);
          }}
        >
          <div
            className="bg-white border border-default shadow-xl w-full max-w-content-md flex flex-col gap-16 p-15 my-24"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-4">
              <h2 className="type-heading-lg">
                {REASON_MODAL_TITLES[reasonModal.kind]}
              </h2>
              <p className="type-body-sm text-ink-muted whitespace-pre-line">
                {reasonModal.confirmMsg}
              </p>
            </div>
            <ReasonPicker
              codes={selectableReasonCodes(REASON_MODAL_ACTION_TYPE[reasonModal.kind])}
              labelFor={reasonCodeLabel}
              value={reasonValue}
              onChange={setReasonValue}
              disabled={pending}
            />
            <div className="flex items-center justify-end gap-10">
              <button
                type="button"
                onClick={() => setReasonModal(null)}
                disabled={pending}
                className="h-36 px-15 bg-sunken text-ink type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmReasonModal}
                disabled={pending || !isReasonValid(reasonValue)}
                className="h-36 px-15 bg-ink text-page type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {pending ? 'Working…' : REASON_MODAL_TITLES[reasonModal.kind]}
              </button>
            </div>
          </div>
        </div>
      )}
      {showFormatSwitch && (
        <SwitchSubscriptionFormatModal
          patientId={patientId}
          subscription={sub}
          onClose={() => setShowFormatSwitch(false)}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Intake tab
// --------------------------------------------------------------------------

export function IntakeTab({ patientId }: { patientId: string }) {
  return <div className="flex flex-col gap-16"><AdminLabRecords patientId={patientId} tab="intake" /><MedicationIntakeTab patientId={patientId} /></div>;
}

function MedicationIntakeTab({ patientId }: { patientId: string }) {
  const { data, isLoading, isError } = useClinicalPatientIntake(patientId);
  // The clinician's labeled view of the same submissions (question text +
  // answers + programmatic checks) so ops reads intake the way Dr. Hinds does.
  const preview = useClinicalPatientIntakePreview(patientId);
  const [open, setOpen] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  if (isLoading) return <LoadingState label="Loading intake…" />;
  if (isError || !data) return <ErrorState>Could not load intake.</ErrorState>;

  const versionedSubmissions = data.master_product_intake?.submissions ?? [];
  const labeledForms = preview.data?.intake_forms ?? [];
  const hasLabeled = labeledForms.length > 0;
  const outliers = hasLabeled ? findIntakeOutliers(labeledForms) : [];
  const submissionCount =
    (hasLabeled ? labeledForms.length : data.submissions.length) +
    versionedSubmissions.length;

  const rawSubmissions = (
    <div className="flex flex-col">
      {data.submissions.map((s) => (
        <div key={s.id} className="border-t border-line-subtle first:border-0">
          <button
            type="button"
            aria-expanded={open === s.id}
            onClick={() => setOpen(open === s.id ? null : s.id)}
            className="w-full flex items-center justify-between gap-12 px-15 py-12 hover:bg-sunken"
          >
            <span className="type-label-sm font-medium">
              {s.form_slug || 'intake'}
            </span>
            <span className="type-label-sm text-ink-muted">
              {fmtDateTime(s.submitted_at)}
            </span>
          </button>
          {open === s.id && (
            <div className="px-15 pb-12">
              <JsonBlock label="Answers" value={s.answers} />
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="ph-no-capture flex flex-col gap-16">
      <Card title="Intake status">
        <div className="px-15 py-12 type-label-sm">
          Medication onboarding:{' '}
          <span className="font-medium">
            {onboardingLabel(data.onboarding_state)}
          </span>
        </div>
      </Card>

      {outliers.length > 0 && (
        <Card title="Programmatic intake checks">
          <div className="px-15 py-12">
            <IntakeOutlierList outliers={outliers} emptyLabel={null} />
          </div>
        </Card>
      )}

      <Card title={`Submitted intake forms (${submissionCount})`}>
        {submissionCount === 0 ? (
          <EmptyState>No intake submissions on record.</EmptyState>
        ) : (
          <div className="flex flex-col">
            {versionedSubmissions.map((submission) => (
              <IntakeEvidenceDetails
                key={submission.id}
                submission={submission}
                submissions={versionedSubmissions}
              />
            ))}
            {hasLabeled ? (
              <>
                {labeledForms.map((form) => (
                  <div
                    key={form.id}
                    className="border-t border-line-subtle first:border-0 px-15 py-12"
                  >
                    <IntakeFormAnswers form={form} />
                  </div>
                ))}
                {data.submissions.length > 0 && (
                  <div className="border-t border-line-subtle">
                    <button
                      type="button"
                      aria-expanded={showRaw}
                      onClick={() => setShowRaw((current) => !current)}
                      className="w-full flex items-center justify-between gap-12 px-15 py-10 hover:bg-sunken"
                    >
                      <span className="type-label-sm text-ink-muted">
                        Raw answers ({data.submissions.length})
                      </span>
                      <span className="type-label-sm text-accent">
                        {showRaw ? 'Hide' : 'Show'}
                      </span>
                    </button>
                    {showRaw && rawSubmissions}
                  </div>
                )}
              </>
            ) : (
              rawSubmissions
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------------
// Documents tab
// --------------------------------------------------------------------------

export function DocumentsTab({ patientId }: { patientId: string }) {
  const { data, isLoading, isError } = useClinicalPatientDocuments(patientId);

  return (
    <div className="flex flex-col gap-15">
      <AdminDocumentUploadCard patientId={patientId} />
      <AdminLabRecords patientId={patientId} tab="documents" />
      {isLoading ? (
        <LoadingState label="Loading documents…" />
      ) : isError || !data ? (
        <ErrorState>Could not load documents.</ErrorState>
      ) : data.documents.length === 0 ? (
        <Card>
          <EmptyState>No documents uploaded.</EmptyState>
        </Card>
      ) : (
        <Card title={`Documents (${data.documents.length})`}>
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken">
                <tr>
                  <Th>Purpose</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Uploaded</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {data.documents.map((d) => (
                  <tr key={d.id} className="border-t border-line-subtle">
                    <Td>
                      <Badge tone={d.purpose === 'id' ? 'accent' : 'neutral'}>
                        {d.purpose || 'other'}
                      </Badge>
                    </Td>
                    <Td>{d.mime || '—'}</Td>
                    <Td>{d.status || '—'}</Td>
                    <Td>{fmtDate(d.created_at)}</Td>
                    <Td>
                      {d.download_url ? (
                        <a
                          href={d.download_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-accent hover:underline"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * Admin uploads a document on the patient's behalf. The common case is
 * an ID the patient couldn't get through the portal — admin grabs the
 * file from email/text/call and uploads here. Two-step under the hood
 * (signed PUT then complete), but presented as a single Choose-file +
 * Upload action.
 */
function AdminDocumentUploadCard({ patientId }: { patientId: string }) {
  const upload = useAdminUploadDocument(patientId);
  const [file, setFile] = useState<File | null>(null);
  const [purpose, setPurpose] = useState<AdminDocumentPurpose>('id');
  const [toast, setToast] = useState<{
    message: string;
    tone: 'good' | 'danger';
  } | null>(null);

  const onUpload = async () => {
    if (!file || upload.isPending) return;
    try {
      await upload.mutateAsync({ file, purpose });
      setFile(null);
      setToast({ message: 'Document uploaded.', tone: 'good' });
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : 'Upload failed. Please try again.';
      setToast({ message: msg.slice(0, 200), tone: 'danger' });
    }
  };

  return (
    <>
      <Card title="Upload on behalf of patient">
        <div className="flex flex-col gap-12 p-15">
          <p className="type-label-sm text-ink-muted">
            Use when a patient can&apos;t complete their own upload (typical:
            ID couldn&apos;t process on mobile). The document goes to
            Healthie and shows in the table below.
          </p>
          <div className="flex flex-wrap items-center gap-10">
            <label className="flex flex-col gap-4">
              <span className="font-mono uppercase text-12 text-ink-muted">
                Purpose
              </span>
              <select
                value={purpose}
                onChange={(e) =>
                  setPurpose(e.target.value as AdminDocumentPurpose)
                }
                disabled={upload.isPending}
                className="h-36 px-10 bg-white border border-line text-13 rounded focus:outline-none focus:border-ink"
              >
                <option value="id">ID</option>
                <option value="lab">Lab</option>
                <option value="prescription">Prescription</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="flex flex-col gap-4 flex-1 min-w-[18rem]">
              <span className="font-mono uppercase text-12 text-ink-muted">
                File
              </span>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={upload.isPending}
                className="h-36 px-10 py-6 bg-white border border-line text-13 rounded file:mr-10 file:bg-sunken file:border-0 file:px-10 file:py-4 file:type-label-sm file:cursor-pointer"
              />
            </label>
            <button
              type="button"
              onClick={onUpload}
              disabled={!file || upload.isPending}
              className="h-36 px-15 bg-ink text-page type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed self-end"
            >
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </button>
          </div>
          {file && !upload.isPending && (
            <div className="type-label-sm text-ink-muted">
              Selected: {file.name} ({Math.round(file.size / 1024)} KB)
            </div>
          )}
        </div>
      </Card>
      {toast && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onDone={() => setToast(null)}
        />
      )}
    </>
  );
}
